import { AxiosResponse } from "axios";
import apiClient from "./apiClient";

// Ключ для хранения блоков в localStorage
const STORAGE_KEY = "page_blocks";
const STORAGE_INDEX_KEY = "page_blocks_index";

// Интерфейсы для работы с блоками страниц
export interface PageBlock {
  id?: number | string;
  name: string;
  html: string;
  css: string;
  js: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface PageBlockResponse {
  id: number | string;
  name: string;
  html: string;
  css: string;
  js: string;
  createdAt: string;
  updatedAt: string;
}

// Вспомогательные функции для работы с localStorage
const getBlocksFromStorage = (): Record<string, PageBlockResponse> => {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    return data ? JSON.parse(data) : {};
  } catch (error) {
    console.error("Ошибка чтения из localStorage:", error);
    return {};
  }
};

const saveBlocksToStorage = (blocks: Record<string, PageBlockResponse>): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(blocks));
  } catch (error) {
    console.error("Ошибка записи в localStorage:", error);
    throw new Error("Не удалось сохранить блок. Возможно, не хватает места в хранилище.");
  }
};

const generateId = (): string => {
  const index = parseInt(localStorage.getItem(STORAGE_INDEX_KEY) || "1", 10);
  const newIndex = index + 1;
  localStorage.setItem(STORAGE_INDEX_KEY, String(newIndex));
  return String(index);
};

// Интерфейс для метрик (события кликов)
export interface PageBlockMetric {
  blockId: number | string;
  elementId?: string;
  elementType: string; // 'button', 'link', 'chip', etc.
  elementText?: string;
  action: string; // 'click', 'view', etc.
  timestamp: string;
  metadata?: Record<string, any>; // Дополнительные данные
}

// Ключ для хранения метрик в localStorage
const METRICS_STORAGE_KEY = "page_blocks_metrics";

// Отправка метрики (сохранение в localStorage)
export const sendPageBlockMetric = async (
  payload: PageBlockMetric
): Promise<void> => {
  try {
    // Получаем существующие метрики
    const metricsJson = localStorage.getItem(METRICS_STORAGE_KEY);
    const metrics: PageBlockMetric[] = metricsJson ? JSON.parse(metricsJson) : [];
    
    // Добавляем новую метрику
    metrics.push(payload);
    
    // Сохраняем обратно (ограничиваем до 1000 метрик, чтобы не переполнить хранилище)
    const limitedMetrics = metrics.slice(-1000);
    localStorage.setItem(METRICS_STORAGE_KEY, JSON.stringify(limitedMetrics));
    
    console.log("Метрика сохранена в localStorage:", payload);
  } catch (error) {
    console.error("Ошибка при отправке метрики:", error);
    // Не пробрасываем ошибку, чтобы не ломать работу страницы
  }
};

// Создание нового блока (сохранение в localStorage)
export const createPageBlock = async (
  payload: PageBlock
): Promise<PageBlockResponse> => {
  try {
    const blocks = getBlocksFromStorage();
    const id = generateId();
    const now = new Date().toISOString();
    
    const newBlock: PageBlockResponse = {
      id,
      name: payload.name,
      html: payload.html,
      css: payload.css,
      js: payload.js,
      createdAt: now,
      updatedAt: now,
    };
    
    blocks[id] = newBlock;
    saveBlocksToStorage(blocks);
    
    console.log("Блок сохранен в localStorage:", id);
    return newBlock;
  } catch (error) {
    console.error("Ошибка при создании блока страницы:", error);
    throw error;
  }
};

// Обновление блока (обновление в localStorage)
export const updatePageBlock = async (
  id: number | string,
  payload: Partial<PageBlock>
): Promise<PageBlockResponse> => {
  try {
    const blocks = getBlocksFromStorage();
    const blockId = String(id);
    
    if (!blocks[blockId]) {
      throw new Error(`Блок с ID ${blockId} не найден`);
    }
    
    const existingBlock = blocks[blockId];
    const updatedBlock: PageBlockResponse = {
      ...existingBlock,
      ...payload,
      id: blockId,
      updatedAt: new Date().toISOString(),
    };
    
    blocks[blockId] = updatedBlock;
    saveBlocksToStorage(blocks);
    
    console.log("Блок обновлен в localStorage:", blockId);
    return updatedBlock;
  } catch (error) {
    console.error("Ошибка при обновлении блока страницы:", error);
    throw error;
  }
};

// Получение блока по ID (из localStorage)
export const getPageBlock = async (
  id: number | string
): Promise<PageBlockResponse> => {
  try {
    const blocks = getBlocksFromStorage();
    const blockId = String(id);
    const block = blocks[blockId];
    
    if (!block) {
      throw new Error(`Блок с ID ${blockId} не найден`);
    }
    
    return block;
  } catch (error) {
    console.error("Ошибка при получении блока страницы:", error);
    throw error;
  }
};

// Получение списка всех блоков (из localStorage)
export const getPageBlocksList = async (): Promise<PageBlockResponse[]> => {
  try {
    const blocks = getBlocksFromStorage();
    return Object.values(blocks).sort((a, b) => {
      // Сортируем по дате обновления (новые первыми)
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    });
  } catch (error) {
    console.error("Ошибка при получении списка блоков:", error);
    throw error;
  }
};

// Удаление блока (из localStorage)
export const deletePageBlock = async (
  id: number | string
): Promise<void> => {
  try {
    const blocks = getBlocksFromStorage();
    const blockId = String(id);
    
    if (!blocks[blockId]) {
      throw new Error(`Блок с ID ${blockId} не найден`);
    }
    
    delete blocks[blockId];
    saveBlocksToStorage(blocks);
    
    console.log("Блок удален из localStorage:", blockId);
  } catch (error) {
    console.error("Ошибка при удалении блока страницы:", error);
    throw error;
  }
};

